/**
 * Noticing files changed by other programs.
 *
 * The desktop watched the open folder with chokidar. A browser has no general
 * way to watch files, so this looks instead: at every file the page has read,
 * whenever the tab comes back into view, and every few seconds while it is in
 * view. It reports what it finds in the desktop watcher's own shape, so the
 * renderer's handling (reload a clean document, ask about a dirty one, keep a
 * deleted file's tab, follow a rename) is the desktop's, unchanged.
 *
 * Where the browser has FileSystemObserver, it is used for the open folder as
 * an early signal: a change it reports is looked at straight away.
 *
 * Unlike the desktop, a file opened on its own is watched too: there is no
 * cost to it here, and it closes a gap the desktop documents as known.
 */
import type { WatchEvent } from './types'
import { fileFor, PermissionNeededError } from './handles'
import { parsePath } from './paths'

type Listener = (events: WatchEvent[]) => void
const listeners = new Set<Listener>()

/** Every file this page has read or written, with the time it then had. */
const known = new Map<string, number>()
/** Files found missing on the last look, so a deletion is reported once. */
const missing = new Set<string>()

const POLL_MS = 4000

/** Something that can list the open folder's markdown files, set by the workspace. */
let listFolder: (() => Promise<Map<string, number>>) | null = null
let folderFiles: Map<string, number> | null = null

export function onWatchEvents(fn: Listener): () => void {
  listeners.add(fn)
  ensureRunning()
  return () => listeners.delete(fn)
}

/** Records a file's time as the page now knows it: after a read or a save. */
export function track(path: string, mtimeMs: number): void {
  const p = parsePath(path)
  if (!p || p.scheme === 'copy' || p.scheme === 'download') return
  known.set(path, mtimeMs)
  missing.delete(path)
}

/** Stops looking at a file, for one that moved or was deleted from the app. */
export function untrack(path: string): void {
  known.delete(path)
  missing.delete(path)
}

/** Starts or stops following a folder's files appearing and going. */
export function watchFolder(list: (() => Promise<Map<string, number>>) | null): void {
  listFolder = list
  folderFiles = null
  observeFolder(null)
  if (list)
    void list()
      .then((files) => (folderFiles = files))
      .catch(() => {})
}

function emit(events: WatchEvent[]): void {
  if (events.length === 0) return
  for (const fn of listeners) fn(events)
}

let looking = false

/** One look at everything. Runs at most one at a time. */
export async function lookNow(): Promise<void> {
  if (looking) return
  looking = true
  try {
    const events: WatchEvent[] = []

    for (const [path, mtime] of known) {
      try {
        // Never asks: a look runs with no click behind it.
        const file = await fileFor(path, { ask: false })
        if (missing.delete(path)) {
          known.set(path, file.lastModified)
          events.push({ kind: 'added', path })
        } else if (file.lastModified !== mtime) {
          known.set(path, file.lastModified)
          events.push({ kind: 'changed', path })
        }
      } catch (err) {
        if (err instanceof PermissionNeededError) continue
        if (/ENOENT/.test(String(err)) && !missing.has(path)) {
          missing.add(path)
          events.push({ kind: 'removed', path })
        }
      }
    }

    if (listFolder && folderFiles) {
      try {
        const now = await listFolder()
        for (const path of now.keys()) {
          if (!folderFiles.has(path) && !events.some((e) => e.path === path)) {
            events.push({ kind: 'added', path })
          }
        }
        for (const path of folderFiles.keys()) {
          if (!now.has(path) && !events.some((e) => e.path === path)) {
            events.push({ kind: 'removed', path })
          }
        }
        folderFiles = now
      } catch {
        // The folder cannot be read just now (permission, or it has gone).
      }
    }

    emit(events)
  } finally {
    looking = false
  }
}

let timer: number | undefined

function ensureRunning(): void {
  if (timer !== undefined || typeof window === 'undefined') return
  const tick = (): void => {
    if (document.visibilityState === 'visible') void lookNow()
  }
  timer = window.setInterval(tick, POLL_MS)
  document.addEventListener('visibilitychange', tick)
  window.addEventListener('focus', tick)
}

// --- FileSystemObserver, where there is one --------------------------------------

interface ObserverLike {
  observe(handle: FileSystemHandle, opts?: { recursive?: boolean }): Promise<void>
  disconnect(): void
}

let observer: ObserverLike | null = null

/** Asks the browser to say when something in the folder changes, if it can. */
export function observeFolder(handle: FileSystemDirectoryHandle | null): void {
  observer?.disconnect()
  observer = null
  const Ctor = (window as unknown as { FileSystemObserver?: new (cb: () => void) => ObserverLike })
    .FileSystemObserver
  if (!handle || !Ctor) return
  try {
    observer = new Ctor(() => void lookNow())
    void observer.observe(handle, { recursive: true }).catch(() => {
      observer = null
    })
  } catch {
    observer = null
  }
}
